import type {
  ContentFindListCreatorDto,
  ContentFindOneCreatorDto,
  CreatorFindContentTagsListCreatorDto,
  CreatorFindOneOutputDto,
  WebCreatorFindContentTagsOutputDto,
  WebCreatorFindListOutputDto,
  WebCreatorFindTagsContentItemOutputDto,
  WebCreatorGlobalSearchOutputDto,
} from '@bldsoft/ovp-api-types'
import type {
  Creator,
  CreatorSocialMediaLink,
  CreatorTag,
} from '@setplex/tria-api'
import { toDate } from '../../tools'
import {
  formatFromCreatorGlobalSearchContent,
  formatFromCreatorTagsContentItem,
} from '../content/format'
import { format as formatTvShow } from '../tvshows/format'
import type {
  ApiCreator,
  ApiCreatorSocialMediaLink,
  ApiCreatorTag,
} from './index.h'

type ContentNestedCreator =
  | ContentFindOneCreatorDto
  | ContentFindListCreatorDto
  | CreatorFindContentTagsListCreatorDto

const formatSocialLink = (
  link: ApiCreatorSocialMediaLink
): CreatorSocialMediaLink | null => {
  if (!link?.socialMedia) {
    return null
  }

  return {
    alias: link.socialMedia.alias,
    title: link.socialMedia.title,
    link: link.link,
  }
}

const formatSafeSocialLinks = (
  links?: readonly ApiCreatorSocialMediaLink[] | null
): CreatorSocialMediaLink[] | undefined => {
  if (!links?.length) {
    return undefined
  }

  const formatted = links
    .map(formatSocialLink)
    .filter((link): link is CreatorSocialMediaLink => link !== null)

  return formatted.length ? formatted : undefined
}

const formatFromFindOne = ({
  id,
  name,
  avatar,
  about,
  background,
  createDate,
  organization,
  organizationDescription,
  socialMediaLinks,
  awards,
}: CreatorFindOneOutputDto): Creator => ({
  id,
  name,
  avatar,
  about,
  background,
  createDate: toDate(createDate),
  organization,
  organizationDescription,
  links: formatSafeSocialLinks(socialMediaLinks),
  awards: awards ? [...awards] : [],
})

const formatFromContentNested = (creator: ContentNestedCreator): Creator => ({
  id: creator.id,
  name: creator.name,
  avatar: creator.avatar,
  about: creator.about,
  organization: 'organization' in creator ? creator.organization : undefined,
  organizationDescription:
    'organizationDescription' in creator
      ? creator.organizationDescription
      : undefined,
  links: formatSafeSocialLinks(creator.socialMediaLinks),
  createDate: new Date(0),
})

const formatFromList = ({
  id,
  name,
  avatar,
  createDate,
}: WebCreatorFindListOutputDto): Creator => ({
  id,
  name,
  avatar,
  createDate: toDate(createDate),
})

const formatFromMinimal = ({
  id,
  name,
  avatar,
  about,
}: {
  id: number
  name: string
  avatar: string
  about?: string
}): Creator => ({
  id,
  name,
  avatar,
  about,
  createDate: new Date(0),
})

const formatFromGlobalSearch = ({
  id,
  name,
  avatar,
  about,
  organization,
  organizationDescription,
  socialMediaLinks,
  content,
  tvShows,
}: WebCreatorGlobalSearchOutputDto): Creator => ({
  id,
  name,
  avatar,
  about,
  organization,
  organizationDescription,
  createDate: new Date(0),
  links: formatSafeSocialLinks(socialMediaLinks),
  content: content?.length
    ? content.map(formatFromCreatorGlobalSearchContent)
    : undefined,
  tvShows: tvShows?.length ? tvShows.map(formatTvShow) : undefined,
})

const isGlobalSearchCreator = (
  creator: ApiCreator
): creator is WebCreatorGlobalSearchOutputDto => 'content' in creator

const isFindOneCreator = (
  creator: ApiCreator
): creator is CreatorFindOneOutputDto => 'awards' in creator

const isListCreator = (
  creator: ApiCreator
): creator is WebCreatorFindListOutputDto =>
  'createDate' in creator && !('about' in creator) && !('awards' in creator)

const isContentNestedCreator = (
  creator: ApiCreator
): creator is ContentNestedCreator =>
  'socialMediaLinks' in creator && !('createDate' in creator)

export const format = (creator: ApiCreator): Creator => {
  if (isGlobalSearchCreator(creator)) {
    return formatFromGlobalSearch(creator)
  }

  if (isFindOneCreator(creator)) {
    return formatFromFindOne(creator)
  }

  if (isListCreator(creator)) {
    return formatFromList(creator)
  }

  if (isContentNestedCreator(creator)) {
    return formatFromContentNested(creator)
  }

  return formatFromMinimal(creator)
}

const formatContentFromCreatorTag = (
  content: WebCreatorFindTagsContentItemOutputDto
) => formatFromCreatorTagsContentItem(content)

const isContentCreatorTag = (
  tag: ApiCreatorTag
): tag is WebCreatorFindContentTagsOutputDto => 'createDate' in tag

export const formatCreatorTag = (tag: ApiCreatorTag): CreatorTag => {
  if (!isContentCreatorTag(tag)) {
    return {
      id: tag.id,
      title: tag.title,
      createDate: new Date(0),
    }
  }

  return {
    id: tag.id,
    title: tag.title,
    description: tag.description,
    horizontalImage: tag.horizontalImage,
    background: tag.background[0],
    content: tag.content?.results.length
      ? tag.content.results.map(formatContentFromCreatorTag)
      : undefined,
    createDate: toDate(tag.createDate),
  }
}
